import { useState, useCallback, useRef, useEffect } from 'react';
import L from 'leaflet';
import { enhancedGetPolygonArea } from '../../MapWithDrawing/Measure/mapUtils';

// Haversine-based line length in feet
const calculateLineLengthFeet = (paths) => {
  if (!paths || paths.length < 2) return 0;
  let totalMeters = 0;
  for (let i = 1; i < paths.length; i++) {
    const p1 = L.latLng(paths[i - 1].lat, paths[i - 1].lng);
    const p2 = L.latLng(paths[i].lat, paths[i].lng);
    totalMeters += p1.distanceTo(p2);
  }
  return totalMeters * 3.28084;
};

/**
 * Extract the updated geometry from a Leaflet layer after an edit and
 * return the patch to merge into the area object (paths + recalculated metrics).
 */
function computeAreaPatchFromLayer(layer, area) {
  if (!layer) return null;

  // Point marker — just the new lat/lng
  if (layer instanceof L.Marker) {
    const ll = layer.getLatLng();
    return {
      paths: [{ lat: ll.lat, lng: ll.lng }],
    };
  }

  // Circle — center + radius
  if (layer instanceof L.Circle) {
    const center = layer.getLatLng();
    const radiusMeters = layer.getRadius();
    const radiusFeet = radiusMeters * 3.28084;
    const areaSF = Math.PI * radiusFeet * radiusFeet;
    const perimeterFeet = 2 * Math.PI * radiusFeet;
    return {
      paths: [{ lat: center.lat, lng: center.lng }],
      radiusX: parseFloat(radiusFeet.toFixed(2)),
      radiusY: parseFloat(radiusFeet.toFixed(2)),
      area: areaSF.toFixed(2),
      areaAcres: (areaSF / 43560).toFixed(4),
      perimeter: perimeterFeet.toFixed(2),
    };
  }

  // Polygon (not polyline — polygon extends polyline in Leaflet)
  if (layer instanceof L.Polygon) {
    let latlngs = layer.getLatLngs();
    if (latlngs[0] && Array.isArray(latlngs[0])) {
      // Nested (e.g. MultiPolygon) — flatten one level
      latlngs = latlngs[0];
    }
    const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
    const measurements = enhancedGetPolygonArea(paths);
    return {
      paths,
      area: (measurements.areaFeet || 0).toFixed(2),
      areaAcres: (measurements.areaAcres || 0).toFixed(4),
      perimeter: (measurements.perimeterFeet || 0).toFixed(2),
    };
  }

  // Polyline — recompute length + derived area from width
  if (layer instanceof L.Polyline) {
    const latlngs = layer.getLatLngs();
    const paths = latlngs.map(ll => ({ lat: ll.lat, lng: ll.lng }));
    const lengthFeet = calculateLineLengthFeet(paths);
    const width = parseFloat(area?.width) || 0;
    const areaSF = lengthFeet * width;
    return {
      paths,
      length: lengthFeet.toFixed(2),
      area: areaSF.toFixed(2),
      areaAcres: (areaSF / 43560).toFixed(4),
    };
  }

  return null;
}

/**
 * Manages "edit shape" mode for a single area. Enables Leaflet.Draw's
 * native editing (vertex drag + whole-shape drag for polygons/lines,
 * center drag + edge resize for circles, drag-to-move for markers)
 * and writes updates back to the area on every edit event.
 */
export default function useAreaEditing({ mapRef, layerMapRef, areas, updateArea }) {
  const [editingAreaId, setEditingAreaId] = useState(null);
  const editingLayerRef = useRef(null);
  const markerMoveHandlerRef = useRef(null);

  // Helper: find the area in the current state
  const getArea = useCallback((areaId) => {
    return areas.find(a => (a._id || a.id) === areaId);
  }, [areas]);

  // Start edit mode on a specific area
  const startEdit = useCallback((areaId) => {
    const layer = layerMapRef.current?.[areaId];
    if (!layer) return;
    const area = getArea(areaId);
    if (!area) return;

    // Stop any previous edit first
    if (editingLayerRef.current && editingLayerRef.current !== layer) {
      try { editingLayerRef.current.editing?.disable?.(); } catch (_) {}
      if (editingLayerRef.current instanceof L.Marker) {
        try { editingLayerRef.current.dragging?.disable?.(); } catch (_) {}
      }
    }

    editingLayerRef.current = layer;
    setEditingAreaId(areaId);

    // Marker: enable native dragging
    if (layer instanceof L.Marker) {
      if (!layer.dragging) {
        // Marker created with default options — need to recreate with draggable
        // For now, try enabling if it exists
        try { layer.options.draggable = true; } catch (_) {}
      }
      try { layer.dragging?.enable?.(); } catch (_) {}

      // Listen for dragend and update the area
      const onDragEnd = () => {
        const patch = computeAreaPatchFromLayer(layer, area);
        if (patch) updateArea(areaId, patch);
      };
      layer.on('dragend', onDragEnd);
      markerMoveHandlerRef.current = { layer, handler: onDragEnd };
      return;
    }

    // Polygon, polyline, circle — enable Leaflet.Draw editing
    if (layer.editing?.enable) {
      try {
        layer.editing.enable();
      } catch (e) {
        console.warn('Failed to enable editing:', e);
      }
    }

    // Listen for edit events on the layer itself
    const onEdit = () => {
      const patch = computeAreaPatchFromLayer(layer, area);
      if (patch) updateArea(areaId, patch);
    };
    // Leaflet.Draw fires edit:edited on the map, but individual layer edits
    // also fire `edit` on the layer for polygons/polylines via editing handler
    layer.on('edit', onEdit);
    // For circles, resize fires 'edit' too via editing handler
    // Keep the handler registered so we can remove it on stopEdit
    markerMoveHandlerRef.current = { layer, handler: onEdit };
  }, [layerMapRef, getArea, updateArea]);

  // Stop editing — commit final state and disable handles
  const stopEdit = useCallback(() => {
    const layer = editingLayerRef.current;
    if (!layer) {
      setEditingAreaId(null);
      return;
    }

    // Capture one final patch before tearing down
    const currentAreaId = editingAreaId;
    if (currentAreaId) {
      const area = getArea(currentAreaId);
      if (area) {
        const patch = computeAreaPatchFromLayer(layer, area);
        if (patch) updateArea(currentAreaId, patch);
      }
    }

    // Remove our event handler
    if (markerMoveHandlerRef.current?.layer && markerMoveHandlerRef.current?.handler) {
      const { layer: h, handler } = markerMoveHandlerRef.current;
      try { h.off('edit', handler); } catch (_) {}
      try { h.off('dragend', handler); } catch (_) {}
    }
    markerMoveHandlerRef.current = null;

    // Disable editing / dragging
    if (layer instanceof L.Marker) {
      try { layer.dragging?.disable?.(); } catch (_) {}
    } else if (layer.editing?.disable) {
      try { layer.editing.disable(); } catch (_) {}
    }

    editingLayerRef.current = null;
    setEditingAreaId(null);
  }, [editingAreaId, getArea, updateArea]);

  // Cleanup on unmount — make sure we don't leave editing handlers attached
  useEffect(() => {
    return () => {
      if (editingLayerRef.current) {
        try {
          if (editingLayerRef.current instanceof L.Marker) {
            editingLayerRef.current.dragging?.disable?.();
          } else {
            editingLayerRef.current.editing?.disable?.();
          }
        } catch (_) {}
      }
    };
  }, []);

  // Escape key exits edit mode
  useEffect(() => {
    if (!editingAreaId) return;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || document.activeElement?.isContentEditable) return;
      e.preventDefault();
      stopEdit();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editingAreaId, stopEdit]);

  return {
    editingAreaId,
    startEdit,
    stopEdit,
  };
}
